import React, { memo, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { objects, strings } from 'investira.sdk';
import { Box, Card, CardContent, Icon, Stack, Typography } from 'investira.react.components';
import { BaseTemplate } from '../../components/templates';
import { SearchEmpresas } from '../../components/organisms';
import services from '../../services';
import { ROUTES_SETORES } from '../../enums';

/** Ordena os setores pelo nome apresentado nos cards. */
function pvGetSetores() {
  return Object.entries(ROUTES_SETORES).sort(([, pFirst], [, pSecond]) =>
    pFirst.descricao.localeCompare(pSecond.descricao, 'pt-BR')
  );
}

/**
 * Apresenta os setores disponíveis antes da listagem de empresas.
 * URLs legadas com query são encaminhadas para a busca ou para o setor correspondente.
 *
 * @returns {JSX.Element} Página de entrada com pesquisa e cards de setores.
 */
const ListaEmpresas = memo(() => {
  const xLocation = useLocation();
  const [isPageLoading] = useState(false);
  const xQuery = strings.querystringToObject(xLocation.search) || {};

  if (Object.keys(xQuery).length > 0) {
    const { top_setor_id: xSetorId, ...xRemainingQuery } = xQuery;
    const xSearch = objects.objectToQueryString(xSetorId ? xRemainingQuery : xQuery);
    const xSetorRoute = Object.entries(ROUTES_SETORES).find(
      ([, pSetor]) => pSetor.setor_id === xSetorId
    )?.[0];
    const xPath = xSetorRoute ? `/empresas/${xSetorRoute}` : '/empresas/busca';

    return <Navigate replace to={`${xPath}${xSearch}`} />;
  }

  return (
    <BaseTemplate
      title="Lista de Empresas"
      description="Lista das empresas. Conheça o patrimônio líquido, capital social, balanço, ativos relacionados, filiais e participação societária."
      searchComponent={
        <SearchEmpresas dataRead={services.pesquisas.pessoa} pageIsLoading={isPageLoading} />
      }
      maxWidth="xl">
      <Box
        role="list"
        aria-label="Setores disponíveis para consulta de empresas"
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(1, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)'
          },
          gap: 2
        }}>
        {pvGetSetores().map(([pRoute, pSetor]) => (
          <Link
            key={pSetor.setor_id}
            to={`/empresas/${pRoute}`}
            aria-label={`Ver empresas do setor ${pSetor.descricao}`}>
            <Card sx={{ height: '100%', display: 'flex' }}>
              <CardContent
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  flexGrow: 1,
                  p: 3
                }}>
                <Stack gap="16px">
                  <Icon iconName={pSetor.iconName} color="primary" size={32} />
                  <Stack>
                    <Typography variant="h6" component="h2" color="textPrimary">
                      {pSetor.descricao}
                    </Typography>
                    <Typography variant="caption" component="h2" color="textPrimary" gutterBottom>
                      {pSetor.metadata.description}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Link>
        ))}
      </Box>
    </BaseTemplate>
  );
});

ListaEmpresas.displayName = 'ListaEmpresas';

export default ListaEmpresas;
